/* eslint-disable react-refresh/only-export-components --
 * Deliberately one module for the dashboard's (range, period) params: the
 * model (URL param helpers, definition builders, range/period pairing) and
 * the selection-controls component that renders it. The tradeoff is that
 * editing this file falls back to a full reload instead of a hot refresh. */
import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
} from "@mui/material";
import classNames from "classnames";
import { z } from "zod";

import {
  filterSchema,
  type Filter,
  type FilterMatchMode,
} from "@/shared/components/ResourceView/Filters/FilterSchema";
import {
  asPeriod,
  asTimeRangeMode,
  CardConfig,
  ChartCardConfig,
  DASHBOARD_SCHEMA_VERSION,
  DashboardDefinition,
  isCustomRange,
  PlacedCardBase,
  PresetRange,
  SectionConfig,
  TIME_RANGES,
  TimeRangeMode,
  TrendCardConfig,
} from "@/shared/domain/dashboards/DashboardDefinition";
import { TimeRange } from "@/shared/domain/dashboards/TimeRange";
import { Period, PERIODS } from "@/shared/domain/metrics";

import styles from "./DashboardParams.module.css";
import { TimeRangeControl } from "./TimeRangeControl";

// The dashboard-definition SCHEMA (types, enums, and the
// `isDashboardDefinition` validator) lives in the shared dashboards domain so
// `DashboardRecord` can use it without importing from this feature. It is
// re-exported here so feature-internal callers keep one import site.
export {
  asTimeRangeMode,
  DASHBOARD_SCHEMA_VERSION,
  isCustomRange,
  isDashboardDefinition,
  isPresetRange,
} from "@/shared/domain/dashboards/DashboardDefinition";
export type {
  AggregatedChartCardConfig,
  CardConfig,
  CardKind,
  ChartCardConfig,
  CustomRange,
  DashboardDefinition,
  Placement,
  PresetRange,
  RawChartCardConfig,
  SectionConfig,
  TrendCardConfig,
} from "@/shared/domain/dashboards/DashboardDefinition";

/**
 * URL search-param keys for the dashboard's top-level configuration — the
 * time range (a preset, or `start`/`end` for a custom window), the
 * aggregation period, and the active filters. Only this configuration
 * lives in the
 * query string; the active saved-dashboard id is a route PATH param
 * (`/metrics/:dashboardId?` — see `normalizeDashboardId` and
 * `Goto.createMetricsDashboardRoute`), and the layout itself (sections +
 * cards) is component state, persisted across reloads as a localStorage
 * draft (see `dashboardDraft.ts`) and snapshotted into a saved
 * `dashboard_definition` on Save.
 */
export const DASHBOARD_PARAMS = {
  range: "range",
  period: "period",
  start: "start",
  end: "end",
  filters: "filters",
} as const;

/** The `range` param's value for a {@link CustomRange}; its bounds travel in
 *  `start`/`end`. Query params are flat strings, so the union is encoded here
 *  as a sentinel plus two siblings and reassembled on read. */
export const CUSTOM_RANGE_PARAM = "custom";

/** Merge `entries` into a copy of `prev`; a `null` value deletes that key. */
export function mergeParams(
  prev: URLSearchParams,
  entries: Record<string, string | null>,
): URLSearchParams {
  const next = new URLSearchParams(prev);
  for (const [key, value] of Object.entries(entries)) {
    if (value === null) {
      next.delete(key);
    } else {
      next.set(key, value);
    }
  }
  return next;
}

/**
 * The `mergeParams` patch that records `range` in the URL.
 *
 * A preset NULLS `start`/`end` (which `mergeParams` deletes), so switching off
 * a custom window can never leave its bounds behind for the next reader — or
 * for anyone the URL is shared with. Every writer goes through here rather
 * than setting `range` directly, so that clearing is not a step a call site
 * can forget.
 */
export function writeRange(
  range: TimeRangeMode,
): Record<string, string | null> {
  return isCustomRange(range)
    ? {
        [DASHBOARD_PARAMS.range]: CUSTOM_RANGE_PARAM,
        [DASHBOARD_PARAMS.start]: range.start,
        [DASHBOARD_PARAMS.end]: range.end,
      }
    : {
        [DASHBOARD_PARAMS.range]: range,
        [DASHBOARD_PARAMS.start]: null,
        [DASHBOARD_PARAMS.end]: null,
      };
}

/**
 * The `range` the URL describes, or `30d` if it describes nothing usable.
 *
 * This is the single boundary where the flat query params become a
 * {@link TimeRangeMode}: `range=custom` reassembles its window from the `start`
 * and `end` siblings, anything else is read as a preset token (so bounds left
 * beside a preset are ignored rather than honoured).
 *
 * A custom range whose bounds are missing, malformed, or reversed degrades to
 * the default rather than rendering an empty dashboard — the same posture
 * {@link isDashboardDefinition} takes for a corrupt stored definition. A
 * hand-edited or truncated URL should still show something.
 */
export function readRange(params: URLSearchParams): TimeRangeMode {
  const raw = params.get(DASHBOARD_PARAMS.range);
  if (raw === CUSTOM_RANGE_PARAM) {
    return (
      asTimeRangeMode({
        kind: "custom",
        start: params.get(DASHBOARD_PARAMS.start),
        end: params.get(DASHBOARD_PARAMS.end),
      }) ?? "30d"
    );
  }
  return asTimeRangeMode(raw) ?? "30d";
}

export function readPeriod(
  params: URLSearchParams,
  range: TimeRangeMode,
): Period {
  return asPeriod(params.get(DASHBOARD_PARAMS.period)) ?? periodForRange(range);
}

const urlFilterSchema = z.object({
  filters: z.array(filterSchema),
  matchMode: z.enum(["AND", "OR"]).default("AND"),
});

export type UrlFilterState = z.infer<typeof urlFilterSchema>;

export const EMPTY_FILTER_STATE: UrlFilterState = {
  filters: [],
  matchMode: "AND",
};

/**
 * Parse the `filters` param. A missing, truncated, or invalid
 * param (e.g. a hand-edited URL) yields {@link EMPTY_FILTER_STATE} rather than
 * throwing, so a broken link degrades to an unfiltered dashboard.
 */
export function readFilterState(params: URLSearchParams): UrlFilterState {
  const raw = params.get(DASHBOARD_PARAMS.filters);
  if (!raw) {
    return EMPTY_FILTER_STATE;
  }
  try {
    const parsed = urlFilterSchema.safeParse(JSON.parse(raw));
    return parsed.success ? parsed.data : EMPTY_FILTER_STATE;
  } catch {
    return EMPTY_FILTER_STATE;
  }
}

/**
 * Serialize filter state, or `null` for an empty list so {@link mergeParams}
 * drops the `filters` key (no dangling `?filters=`).
 */
export function writeFilterState(state: UrlFilterState): string | null {
  return state.filters.length > 0 ? JSON.stringify(state) : null;
}

function withFilterState(
  params: URLSearchParams,
  next: UrlFilterState,
): URLSearchParams {
  return mergeParams(params, {
    [DASHBOARD_PARAMS.filters]: writeFilterState(next),
  });
}

export function appendFilter(
  params: URLSearchParams,
  filter: Filter,
): URLSearchParams {
  const state = readFilterState(params);
  return withFilterState(params, {
    ...state,
    filters: [...state.filters, filter],
  });
}

export function removeFilterAt(
  params: URLSearchParams,
  index: number,
): URLSearchParams {
  const state = readFilterState(params);
  return withFilterState(params, {
    ...state,
    filters: state.filters.filter((_, i) => i !== index),
  });
}

export function updateFilterAt(
  params: URLSearchParams,
  index: number,
  filter: Filter,
): URLSearchParams {
  const state = readFilterState(params);
  return withFilterState(params, {
    ...state,
    filters: state.filters.map((existing, i) =>
      i === index ? filter : existing,
    ),
  });
}

export function clearFilters(params: URLSearchParams): URLSearchParams {
  const state = readFilterState(params);
  return withFilterState(params, { ...state, filters: [] });
}

export function setFilterMatchMode(
  params: URLSearchParams,
  matchMode: FilterMatchMode,
): URLSearchParams {
  const state = readFilterState(params);
  return withFilterState(params, { ...state, matchMode });
}

/**
 * The active saved-dashboard id from the `:dashboardId?` route path param, or
 * `null` when none is selected (the bare `/metrics` or
 * `/devices/:deviceId/metrics` route matches with an `undefined` param). An
 * empty or whitespace-only segment normalizes to `null` HERE, at the single
 * read site, so every consumer — the render gate, `useDashboard` (which
 * disables its query for falsy ids), draft keys, the picker — sees one
 * consistent "no dashboard selected" value instead of a `""` that some layers
 * treat as absent and others as an active selection.
 */
export function normalizeDashboardId(raw: string | undefined): string | null {
  if (raw === undefined) {
    return null;
  }
  const trimmed = raw.trim();
  return trimmed === "" ? null : trimmed;
}

/** Generate a short, URL-safe id for a freshly added card. */
export function generateCardId(): string {
  return `c_${Math.random().toString(36).slice(2, 10)}`;
}

/** Stable id of the single fallback section. Also the id of the sample
 *  dashboard's FIRST seeded section — its later sections suffix it
 *  (`default-section-2`, …); see `sampleDashboard.ts`. */
export const DEFAULT_SECTION_ID = "default-section";

/** The single section a definition with an EMPTY section list gets when
 *  normalized or snapshotted. Deliberately not the sample dashboard's
 *  `defaultSections`: a section-less definition must gain one section to hold
 *  its cards, never a sample dashboard's full seed-section set. */
export function fallbackSections(): SectionConfig[] {
  return [{ id: DEFAULT_SECTION_ID, title: "Section 1" }];
}

/** Generate a short id for a freshly added section. */
export function generateSectionId(): string {
  return `s_${Math.random().toString(36).slice(2, 10)}`;
}

/** The dashboard's canvas state: the ordered section list plus every placed
 *  card. Held as component state by `MetricsDashboard`. */
export interface DashboardLayout {
  sections: SectionConfig[];
  cards: CardConfig[];
}

/**
 * The canvas for a freshly created (empty) dashboard: one empty section
 * ({@link fallbackSections} — the same shape normalization assigns to a
 * section-less definition, so an empty layout round-trips
 * {@link normalizeDefinition} unchanged) and no cards. Seeded by the
 * "New empty dashboard" action and by every unsaved view that did not ask
 * for the sample seed — the picker's "New sample dashboard" row and the
 * priority-3 landing are the only two that do (`sampleDashboard.ts`).
 */
export function emptyDashboardLayout(): DashboardLayout {
  return { sections: fallbackSections(), cards: [] };
}

/** Assign any card without an explicit section to `firstSectionId`. Loading a
 *  card into a sections-aware layout makes its membership EXPLICIT, so later
 *  section reorders can't silently re-home it via the first-section fallback
 *  (the assignment persists into every snapshot built from the state). */
function assignSections(
  cards: CardConfig[],
  firstSectionId: string,
): CardConfig[] {
  return cards.map((c) => (c.section ? c : { ...c, section: firstSectionId }));
}

/** Rebuild a card in canonical field order with only known fields, so JSON
 *  comparisons (dirty checks) are stable regardless of the source's shape. */
function normalizeCard(card: CardConfig): CardConfig {
  const placed: PlacedCardBase = {};
  if (card.placement) {
    const { row, col, w, h } = card.placement;
    placed.placement = { row, col, w, h };
  }
  if (card.section) {
    placed.section = card.section;
  }
  if (card.kind === "trend") {
    return {
      id: card.id,
      kind: "trend",
      metric: card.metric,
      aggregation: card.aggregation,
      ...placed,
    };
  }
  if (card.aggregation === undefined) {
    // A raw chart card: no aggregation, no display, and no placeholder for
    // either. Writing defaulted values here would put fields in the persisted
    // JSONB that the card never reads and the next reader would mistake for a
    // real choice.
    return {
      id: card.id,
      kind: "chart",
      metric: card.metric,
      ...placed,
    };
  }
  return {
    id: card.id,
    kind: "chart",
    metric: card.metric,
    aggregation: card.aggregation,
    display: card.display,
    ...placed,
  };
}

/**
 * Rebuild a {@link DashboardDefinition} in canonical shape: fields in
 * canonical order, only known fields kept, an empty section list re-seeded
 * with the fallback section, and every card's section membership made
 * explicit. Comparing `JSON.stringify(normalizeDefinition(a))` values is the
 * dirty check, so both the loaded record and the live URL snapshot go
 * through this one normalization (a stored record round-trips through
 * backend JSONB, which does not preserve key order).
 */
export function normalizeDefinition(
  definition: DashboardDefinition,
): DashboardDefinition {
  const sections =
    definition.sections.length > 0
      ? definition.sections.map((s) => ({ id: s.id, title: s.title }))
      : fallbackSections();
  return {
    schema_version: DASHBOARD_SCHEMA_VERSION,
    range: definition.range,
    period: definition.period,
    sections,
    cards: assignSections(definition.cards.map(normalizeCard), sections[0].id),
  };
}

/**
 * Snapshot the dashboard's live view-state — `range`/`period` from the URL
 * ({@link readRange} / {@link readPeriod}) plus the in-memory layout state —
 * into the JSONB shape we persist to the backend (and to the localStorage
 * draft). Cards rebuild through {@link normalizeCard} and section membership
 * is made explicit, so `JSON.stringify` comparisons against
 * {@link normalizeDefinition} output (the dirty check) are stable.
 */
export function buildDashboardDefinition(
  params: URLSearchParams,
  sections: SectionConfig[],
  cards: CardConfig[],
): DashboardDefinition {
  const range = readRange(params);
  const safeSections =
    sections.length > 0
      ? sections.map((s) => ({ id: s.id, title: s.title }))
      : fallbackSections();
  return {
    schema_version: DASHBOARD_SCHEMA_VERSION,
    range,
    period: readPeriod(params, range),
    sections: safeSections,
    cards: assignSections(cards.map(normalizeCard), safeSections[0].id),
  };
}

/**
 * Whether two definitions differ in LAYOUT — sections or cards — ignoring
 * the time params (`range`/`period`).
 *
 * This is the comparison behind the INTERRUPTING tier of "dirty": the prompt
 * raised when picking another dashboard would replace unsaved edits. It is
 * the one thing that interrupts the user, so it only acts on a real layout
 * edit. A params-only difference stays SAVEABLE (the full-definition
 * comparison drives the Save affordances) but never interrupts — the URL
 * already carries the user's range/period. Both sides go through {@link normalizeDefinition}, so the
 * comparison is on meaning, not stored bytes.
 */
export function definitionsDifferInLayout(
  a: DashboardDefinition,
  b: DashboardDefinition,
): boolean {
  const layoutOf = (definition: DashboardDefinition): string => {
    const { sections, cards } = normalizeDefinition(definition);
    return JSON.stringify({ sections, cards });
  };
  return layoutOf(a) !== layoutOf(b);
}

/**
 * Overlay a {@link DashboardDefinition}'s top-level configuration — `range`
 * and `period`, the only view-state that lives in the URL — onto `prev`.
 * Used when a saved dashboard record or a localStorage draft seeds the view;
 * the definition's sections/cards seed component state instead. Loaders
 * validate definitions with {@link isDashboardDefinition} before applying,
 * so the values overlay as-is.
 */
export function applyDefinitionTimeParams(
  prev: URLSearchParams,
  definition: DashboardDefinition,
): URLSearchParams {
  return mergeParams(prev, {
    ...writeRange(definition.range),
    [DASHBOARD_PARAMS.period]: definition.period,
  });
}

/**
 * Resolve a trend card's effective metric for rendering. Falls back to an
 * available definition when the card's metric is unset or no longer exists;
 * `seedIndex` (this card's position among ALL unresolved cards, charts
 * included) spreads the seeded defaults across DISTINCT definitions instead
 * of repeating the first one.
 */
export function effectiveTrendMetric(
  card: TrendCardConfig,
  allNames: string[],
  seedIndex = 0,
): string {
  if (card.metric && allNames.includes(card.metric)) {
    return card.metric;
  }
  if (allNames.length === 0) {
    return "";
  }
  return allNames[seedIndex % allNames.length];
}

/**
 * Resolve a chart card's effective metric for rendering. Falls back to an
 * available definition when the card's metric is unset or no longer exists;
 * `seedIndex` works exactly as in {@link effectiveTrendMetric} (one counter
 * spans both kinds), so a fresh dashboard's charts and trends together cover
 * as many distinct definitions as the org has.
 */
export function effectiveChartMetric(
  card: ChartCardConfig,
  allNames: string[],
  seedIndex = 0,
): string {
  if (card.metric && allNames.includes(card.metric)) {
    return card.metric;
  }
  if (allNames.length === 0) {
    return "";
  }
  return allNames[seedIndex % allNames.length];
}

// --- Time-range / period pairing (formerly timeRange.ts) ---------------------
// `TIME_RANGES` (and its `TimeRangeMode` value type) is part of the persisted
// dashboard-definition schema, so it lives in the shared dashboards domain;
// re-exported here so feature-internal callers keep one import site.
export { TIME_RANGES };
export type { TimeRangeMode };

// All (range, period) pairing below is computed from durations in days, so
// the declaration order of TIME_RANGES and PERIODS is presentational history
// only — inserting or reordering an option can never change which pairs fit.
// Anywhere an ordering matters, it is derived by sorting on duration.

/** Nominal length of one aggregation bucket, in days. `quarterly` is 91 (13
 *  weeks) rather than 90 so a 90-day window is NOT considered to hold a full
 *  quarter — see {@link periodFitsRange}. */
export const PERIOD_DAYS: Record<Period, number> = {
  daily: 1,
  weekly: 7,
  monthly: 30,
  quarterly: 91,
  yearly: 365,
};

/**
 * How many whole days a range spans. A preset reads its static `days`; a
 * custom window is measured from its own bounds, INCLUSIVE of both ends
 * (Jan 5 → Jan 20 is 16 days, not 15).
 *
 * The pairing helpers below take a duration rather than a range so they work
 * for a window whose length is only known at runtime.
 */
export function rangeDurationDays(range: TimeRangeMode): number {
  return TimeRange.fromMode(range).durationDays;
}

/** The range options sorted shortest-first by duration. */
const RANGES_BY_DURATION: readonly (typeof TIME_RANGES)[number][] = [
  ...TIME_RANGES,
].sort((a, b) => a.days - b.days);

/** The period options sorted finest-first by duration. */
/**
 * What the period select shows for each period.
 *
 * A lookup rather than a prettified wire value: `daily` is the format the
 * backend and the persisted `dashboard_definition` use, while the control
 * reads as the noun the buckets are — "Day", not "Daily". Typing it as
 * `Record<Period, string>` means a new period cannot be added without also
 * giving it a label.
 */
const PERIOD_LABELS: Record<Period, string> = {
  daily: "Day",
  weekly: "Week",
  monthly: "Month",
  quarterly: "Quarter",
  yearly: "Year",
};

export const PERIODS_BY_DURATION: readonly Period[] = [...PERIODS].sort(
  (a, b) => PERIOD_DAYS[a] - PERIOD_DAYS[b],
);

/**
 * Whether `period` is appropriate for `range`: the window must be strictly
 * longer than one aggregation bucket, so the dashboard always has more than a
 * single data point to show. The dashboard only auto-adjusts the
 * (range, period) pair when this is false, so a deliberately fine period over
 * a long window (more buckets) is left alone.
 */
export function periodFitsDuration(days: number, period: Period): boolean {
  return days > PERIOD_DAYS[period];
}

export function periodFitsRange(range: TimeRangeMode, period: Period): boolean {
  return periodFitsDuration(rangeDurationDays(range), period);
}

/**
 * Coarsest period that still fits `range`. Used to auto-fit the period when
 * the current one no longer fits the chosen window.
 */
export function periodForDuration(days: number): Period {
  const fitting = PERIODS_BY_DURATION.filter((period) =>
    periodFitsDuration(days, period),
  );
  return fitting.length > 0
    ? fitting[fitting.length - 1]
    : PERIODS_BY_DURATION[0];
}

export function periodForRange(range: TimeRangeMode): Period {
  return periodForDuration(rangeDurationDays(range));
}

/**
 * Shortest range that fits `period`. Used to auto-expand the window when it's
 * too short for the chosen period.
 */
export function rangeForPeriod(period: Period): PresetRange {
  const fitting = RANGES_BY_DURATION.find((tr) =>
    periodFitsRange(tr.value, period),
  );
  return (fitting ?? RANGES_BY_DURATION[RANGES_BY_DURATION.length - 1]).value;
}

// --- Params selection controls (formerly MetricsControls.tsx) ----------------

export interface DashboardParamsProps {
  range: TimeRangeMode;
  period: Period;
  onRangeChange: (range: TimeRangeMode) => void;
  onPeriodChange: (period: Period) => void;
  /** Slot-sizing class for the range control, injected by `DashboardHeader`
   *  (which owns all header positioning/sizing). */
  rangeClassName?: string;
  /** Slot-sizing class for the "grouped by" field, ditto. */
  periodClassName?: string;
}

/**
 * The dashboard's global (range, period) selection controls: a segmented row
 * of range presets plus a bucket-size select labelled "Aggregated by".
 *
 * The two controls swapped presentation — the range was a MUI `Select` and the
 * period a button group. The `Select` markup moved across unchanged; the old
 * period buttons were deleted outright, because `ToggleButtonGroup` replaces
 * them and now owns the full/abbreviated label swap that used to live in this
 * module's CSS.
 *
 * Renders ONLY the two control groups, as fragment siblings — no wrapper box.
 * `DashboardHeader` composes them as direct flex items of its header row and
 * injects its slot-sizing classes.
 */
export function DashboardParams({
  range,
  period,
  onRangeChange,
  onPeriodChange,
  rangeClassName,
  periodClassName,
}: DashboardParamsProps) {
  const handlePeriod = (e: SelectChangeEvent) =>
    onPeriodChange(e.target.value as Period);

  return (
    <>
      <TimeRangeControl
        range={range}
        onRangeChange={onRangeChange}
        className={rangeClassName}
      />

      <FormControl
        size="small"
        className={classNames(styles.periodField, periodClassName)}
      >
        <InputLabel id="aggregated-by-label">Aggregated by</InputLabel>
        <Select
          labelId="aggregated-by-label"
          label="Aggregated by"
          value={period}
          onChange={handlePeriod}
        >
          {PERIODS_BY_DURATION.map((p) => (
            <MenuItem key={p} value={p}>
              {PERIOD_LABELS[p]}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </>
  );
}
